import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/setting/default-labs/lab-company/style'

/** API */
import LabCompaniesApi from 'api/setting/default-labs/lab-company.api'

/** UTILS || SLICE */
import { submitModal, unSubmitModal, showModal, resetModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextField from 'component/Input/InputTextField'
import InputTextSearch from 'component/Input/InputTextSearch'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import PageModal from 'new-components/modal/page-modal'
import TextInput from 'new-components/inputs/text-input'
import CheckboxButton from 'new-components/buttons/checkbox-button'
import SwitchButton from 'new-components/buttons/switch-button'
import { Box } from '@mui/material'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  LAB_COMPANY_NAME: ``,
  IS_DUPLICATE_LAB_COMPANY_NAME: ``,
  LAB_COMPANY_NAME_STRING_BASE: ``,
  LAB_COMPANY_NAME_STRING_EMPTY: ``,
  LAB_COMPANY_NAME_MAXIMUM_LENGTH: ``,
  LAB_COMPANY_NAME_ANY_REQUIRED: ``,

  LAB_COMPANY_NAME_EN: ``,
  IS_DUPLICATE_LAB_COMPANY_NAME_EN: ``,
  LAB_COMPANY_NAME_EN_STRING_BASE: ``,
  LAB_COMPANY_NAME_EN_STRING_EMPTY: ``,
  LAB_COMPANY_NAME_EN_MAXIMUM_LENGTH: ``,
  LAB_COMPANY_NAME_EN_ANY_REQUIRED: ``
}
export default function LabCompany() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [labCompanies, setLabCompanies] = useState([])

  const [labCompanyId, setLabCompanyId] = useState(Number)
  const [labCompanyName, setLabCompanyName] = useState('')
  const [labCompanyNameEn, setLabCompanyNameEn] = useState('')
  const [labCompanyStatus, setLabCompanyStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)

  const loadData = useCallback(async () => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await LabCompaniesApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setLabCompanies(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {
    if (!labCompanyName) return setErrorMessage({ ...errorMessage, ...{ LAB_COMPANY_NAME: t('LAB_COMPANY.MESSAGE.LAB_COMPANY_NAME') } })

    dispatch(submitModal())

    LabCompaniesApi.create({ labCompanyName, labCompanyNameEn })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('LAB_COMPANY.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`LAB_COMPANY.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`LAB_COMPANY.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!labCompanyName) return setErrorMessage({ ...errorMessage, ...{ LAB_COMPANY_NAME: t(`LAB_COMPANY.MESSAGE.LAB_COMPANY_NAME`) } })

    dispatch(submitModal())
    LabCompaniesApi.update(labCompanyId, { labCompanyName, labCompanyNameEn, status: labCompanyStatus })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`LAB_COMPANY.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data

          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`LAB_COMPANY.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`LAB_COMPANY.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setLabCompanyId(0)
    setLabCompanyName('')
    setLabCompanyNameEn('')
    setLabCompanyStatus('')
    clearErrorMessage()
    setIsPopup(false)
  }

  const onActive = () => {
    if (id) {
      LabCompaniesApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('LAB_COMPANY.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('LAB_COMPANY.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      LabCompaniesApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('LAB_COMPANY.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('LAB_COMPANY.MESSAGE.ERROR'))
        }
      })
    }
    // const { labCompanyId: lcId, labCompanyName: lcName, labCompanyNameEn: lcNameEn } = data

    // swalActive(
    //   `<p class="mb-0 mx-auto" style="max-width: 325px">${t('LAB_COMPANY.ALERT.CONFIRM_DELETE')}</p>`,
    //   `<p class="mb-0 mx-auto" style="max-width: 325px">${t('LAB_COMPANY.ALERT.CONFIRM_DELETE_', {
    //     labCompanyName: lcName,
    //     labCompanyNameEn: lcNameEn ? ' (' + lcNameEn + ')' : ''
    //   })}</p>`,
    //   (res: any) => {
    //     if (res) {
    //       LabCompaniesApi.remove(lcId)
    //         .then((resp) => {
    //           notiSuccess(t('LAB_COMPANY.MESSAGE.SUCCESS.DELETE'))
    //           loadData()
    //         })
    //         .catch((e) => {
    //           notiError(t('LAB_COMPANY.MESSAGE.ERROR'))
    //         })
    //     }
    //   }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL')
    // )
  }

  const onEdit = (data: any) => {
    const { labCompanyId: lcId, labCompanyName: lcName, labCompanyNameEn: lcNameEn, status: lcStatus } = data
    setLabCompanyId(lcId)
    setLabCompanyName(lcName)
    setLabCompanyNameEn(lcNameEn)
    setLabCompanyStatus(lcStatus)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'labCompanyName', disablePadding: false, label: t('LAB_COMPANY.TABLE.CELL.LAB_COMPANY_NAME') },
    { id: 'labCompanyNameEn', disablePadding: false, label: t('LAB_COMPANY.TABLE.CELL.LAB_COMPANY_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('LAB_COMPANY.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('LAB_COMPANY.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { labCompanyId: lcId, labCompanyName: lcName, labCompanyNameEn: lcNameEn, updatedBy, updatedAt, status: lcStatus } = objData
    const statusBtnActive = lcStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = lcStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: lcId,
      id: lcId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: lcName },
        { option: 'TEXT', align: 'left', label: lcNameEn },
        { option: 'STATUS', align: 'center', label: lcStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.labCompanyName)
    setNameEn(obj.labCompanyNameEn)
  }

  return (
    <div className={classes.root}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`LAB_COMPANY.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`LAB_COMPANY.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { labCompanyName: name, labCompanyNameEn: nameEn && `(${nameEn})`, status: t(`LAB_COMPANY.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <Card className="border-0 h-100">
        <HeaderCard text={t('LAB_COMPANY.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('LAB_COMPANY.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => setIsPopup(true)} textButton={t('LAB_COMPANY.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={labCompanies.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <PageModal
        title={labCompanyId ? t('LAB_COMPANY.TITLE_UPDATE') : t('LAB_COMPANY.TITLE_CREATE')}
        isOpen={isPopup}
        onCancel={{ onClick: () => resetForm() }}
        onSubmit={{ onClick: () => labCompanyId ? submitEdit() : submit() }}
        handleClose={resetForm}
        size={'XS'}
      >
        <Box className="py-2">
          {labCompanyId ? (
            <Form className="mx-0 mt-0 mb-3 p-0 d-inline-flex w-100">
              <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
              <SwitchButton
                onChange={() => setLabCompanyStatus(labCompanyStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                checked={labCompanyStatus === 'ACTIVE' ? true : false}
              />
            </Form>
          ) : (
            ''
          )}

          <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
            <TextInput
              fullWidth
              onChange={(event) => {
                setLabCompanyName(event.target.value)
                clearErrorMessage()
              }}
              value={labCompanyName}
              label={t('LAB_COMPANY.INPUT.LAB_COMPANY_NAME')}
              helperText={
                errorMessage.LAB_COMPANY_NAME ||
                errorMessage.IS_DUPLICATE_LAB_COMPANY_NAME ||
                errorMessage.LAB_COMPANY_NAME_STRING_BASE ||
                errorMessage.LAB_COMPANY_NAME_STRING_EMPTY ||
                errorMessage.LAB_COMPANY_NAME_MAXIMUM_LENGTH ||
                errorMessage.LAB_COMPANY_NAME_ANY_REQUIRED
              }
              required={true}
            />
            <TextInput
              fullWidth
              onChange={(event) => {
                setLabCompanyNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={labCompanyNameEn}
              helperText={
                errorMessage.LAB_COMPANY_NAME_EN ||
                errorMessage.IS_DUPLICATE_LAB_COMPANY_NAME_EN ||
                errorMessage.LAB_COMPANY_NAME_EN_STRING_BASE ||
                errorMessage.LAB_COMPANY_NAME_EN_STRING_EMPTY ||
                errorMessage.LAB_COMPANY_NAME_EN_MAXIMUM_LENGTH ||
                errorMessage.LAB_COMPANY_NAME_EN_ANY_REQUIRED
              }
              label={t('LAB_COMPANY.INPUT.LAB_COMPANY_NAME_EN')}
              required={false}
            />
          </Box>
        </Box>
      </PageModal>

    </div>
  )
}
